import { DynamicTreeDataSource, NodeConfiguration, NodeData } from '../../../ui/gzTree/type';
import { TreeNodeActionHandlersFactory } from '../actions/tree.node.action.handlers.factory';
import { NetworkTreeAction } from '../../networkAppContainer/helpers/types';
import { NODE_ADDED, NodeRequestConfig } from '../../../ui/gzTree/uiElements/gzNodeRenameInput/helpers/types';
import { GzNodeDirectorWebComponent } from '../../../ui/gzTree/uiElements/gzNodeDirector';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';

export class NetworkTreeDataSource extends DynamicTreeDataSource {
  public async renameNode(nodeElement: GzNodeDirectorWebComponent<object>, newName: string) {
    const nodeId: string | number = nodeElement.nodeId;
    const renameActionHandler = TreeNodeActionHandlersFactory.create(NetworkTreeAction.RENAME);
    const node = this.nodes.get(nodeId);

    if (!node) {
      throw new Error('Node not found when renaming');
    }

    const nodeRequest: NodeRequestConfig = {
      nodeData: node,
      changes: {
        name: newName,
      },
    };

    renameActionHandler
      .createNodeRequest(nodeRequest)
      .then(newNodeData => {
        nodeElement.dispatchEvent(
          new CustomEvent(NODE_ADDED, {
            detail: {
              nodeData: newNodeData,
            },
            bubbles: true,
            composed: true,
          })
        );
      })
      .catch(exception => {
        super.renameNode(nodeElement, node.originalData.name);
      });
    super.renameNode(nodeElement, newName);
  }

  public async attachNodeToContainer(
    node: NodeConfiguration,
    containerNode: NodeData,
    addFirst?: boolean
  ): Promise<boolean> {
    this.nodes.set(node.nodeId, { ...node, parentNodeId: containerNode.nodeId });

    if (!containerNode.isOpen) {
      await this.toggleNodeOpen(containerNode, true);
    }

    let updatedContainerNode = this.nodes.get(containerNode.nodeId)!;
    if (!updatedContainerNode.childNodeIds) {
      updatedContainerNode.childNodeIds = [];
    }

    if (updatedContainerNode.childNodeIds?.includes(node.nodeId)) {
      return false;
    }

    if (addFirst) {
      updatedContainerNode.childNodeIds?.unshift(node.nodeId);
    } else {
      const insertIndex = this.getIndexForNewAddedNode(node, updatedContainerNode.childNodeIds || []);
      updatedContainerNode.childNodeIds?.splice(insertIndex, 0, node.nodeId);
    }

    this.emitNodeChanges();
    return true;
  }

  private getIndexForNewAddedNode(newNode: NodeConfiguration, childNodeIds: Array<string>) {
    const newNodeData = newNode.originalData as ProtectedEntity;
    const nodeData = childNodeIds.map(id => this.nodes.get(id));
    let min = 0;
    let max = nodeData.length;

    while (min < max) {
      const mid = Math.floor((min + max) / 2);
      const currentMidNode = nodeData[mid]?.originalData as ProtectedEntity;

      if (
        currentMidNode.type < newNodeData.type ||
        (currentMidNode.type === newNodeData.type &&
          currentMidNode.name.localeCompare(newNodeData.name, Application.Main.getLanguage().replace('_', '-')) < 0)
      ) {
        min = mid + 1;
      } else {
        max = mid;
      }
    }

    return min;
  }
}
